/**
 * Data Visualization - Intermediate Module Challenge Configuration
 *
 * 5 questions covering five advanced chart types:
 * Level 1 - Distribution Analysis       : Histogram, Heatmap     (Q1-Q2)
 * Level 2 - Multi-Variable & Geographic : Map, Box Plot, Scatter  (Q3-Q5)
 *
 * Dataset: E-commerce Platform
 *   customers : customer_id, signup_date, country
 *   orders    : order_id, customer_id, order_date, order_amount, product_category, quantity
 */

export const DATA_VIZ_LEVELS = [
    {
        id: 1,
        title: 'Distribution Analysis',
        description: 'Histogram and heatmap charts for understanding how data is spread',
        color: '#8b5cf6',
    },
    {
        id: 2,
        title: 'Multi-Variable & Geographic',
        description: 'Map, box plot, and scatter charts for spatial and multi-metric analysis',
        color: '#f97316',
    },
];

export const DATA_VIZ_CHALLENGES = [

    // ═══ Level 1 - Distribution Analysis ═══════════════════════════

    {
        id: 'histogram-order-value-dist',
        level: 1,
        title: 'How are order values distributed?',
        businessContext: `The finance team wants to understand **the spread of transaction sizes** on the platform. Are most orders small (under $50) or do customers regularly place large orders? Understanding the distribution of \`order_amount\` helps size payment infrastructure, fraud thresholds, and discount strategies.`,
        question: 'Create a histogram showing the distribution of order amounts.',
        goal: 'Histogram with order amount buckets on the X-axis and order count on the Y-axis.',
        hint: `A histogram groups a continuous number into ranges (buckets) and counts how many values fall in each range.
1. Select the **orders** table.
2. Set Metric to **COUNT** of \`order_id\`.
3. Add a Group By for **order_amount** and set a **bucket size** (try \`50\`).
4. Run the query and select the **Histogram** chart type.`,
        teach: 'Histograms visualise the distribution of a continuous numeric variable by grouping values into equal-width buckets. Unlike a bar chart (which compares categories), a histogram reveals shape - is the data symmetric, skewed, or bimodal?',
        explanation: 'The histogram reveals whether our order values are clustered around a typical amount or spread across a wide range, directly informing pricing and payment strategy decisions.',
        expectedChartType: 'histogram',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: false, join: false, granularity: false, limit: false,
        },
        answer: {
            table: 'orders',
            aggregation: { fn: 'COUNT', column: 'order_id' },
            groupBy: [{ column: 'order_amount', bucket: { size: 50 } }],
            filters: [],
            join: null,
        },
    },

    {
        id: 'heatmap-category-monthly',
        level: 1,
        title: 'Which product categories are most popular each month?',
        businessContext: `The merchandising team wants to see **seasonality by product category** - are Electronics orders highest in December (holiday gifts)? Are Clothing orders steady year-round? A heatmap with months on one axis and categories on the other reveals patterns that a simple line chart would hide.`,
        question: 'Create a heatmap showing order count by month and product category.',
        goal: 'Heatmap - months on one axis, product categories on the other, order count as the value.',
        hint: `A heatmap needs two grouping dimensions and one numeric metric. The colour intensity of each cell shows the value.
1. Select the **orders** table.
2. Set Metric to **COUNT** of \`order_id\`.
3. Add a Group By for **order_date** (granularity: **Month**) and a second Group By for **product_category**.
4. Run the query and select the **Heatmap** chart type.`,
        teach: 'Heatmaps display a matrix of values using colour intensity. They are ideal when you have two categorical (or time-based) dimensions and want to spot hot spots - cells that are unusually high or low - without scanning rows of numbers.',
        explanation: 'The heatmap instantly surfaces seasonal spikes: which categories peak at which times of year, giving the merchandising team a data-driven plan for inventory and promotions.',
        expectedChartType: 'heatmap',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: false, join: false, granularity: true, limit: false,
        },
        answer: {
            table: 'orders',
            aggregation: { fn: 'COUNT', column: 'order_id' },
            groupBy: [{ column: 'order_date', granularity: 'month' }, { column: 'product_category' }],
            filters: [],
            join: null,
        },
    },

    // ═══ Level 2 - Multi-Variable & Geographic ═══════════════════════

    {
        id: 'map-revenue-by-country',
        level: 2,
        title: 'Which countries generate the most revenue?',
        businessContext: `The expansion team is evaluating new markets. Before investing in a new region, they need to know **which countries already generate the most revenue** - these are proven markets worth doubling down on. Revenue isn't stored directly against country, so you'll need to join the \`orders\` and \`customers\` tables to bring it together.`,
        question: 'Create a map chart showing total revenue (sum of order_amount) by country.',
        goal: 'World map - countries shaded by total order revenue.',
        hint: `Country is on the \`customers\` table, but revenue is on \`orders\`. You need to join them.
1. Select the **orders** table.
2. Add a **Join** with **customers** on \`customer_id\`.
3. Set Metric to **SUM** of \`order_amount\`.
4. Add a Group By for **country**.
5. Run the query and select the **Map** chart type.`,
        teach: 'Map charts are choropleth visualisations - each country or region is shaded based on a value. They are the fastest way to communicate geographic patterns. The join connects the revenue data (orders) with the dimension we want to display on the map (country from customers).',
        explanation: 'The map shows our highest-value markets at a glance, allowing the expansion team to quickly identify where to invest sales and marketing resources.',
        expectedChartType: 'map',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: false, join: true, granularity: false, limit: false,
        },
        answer: {
            table: 'orders',
            aggregation: { fn: 'SUM', column: 'order_amount' },
            groupBy: [{ column: 'country' }],
            filters: [],
            join: { table: 'customers', type: 'JOIN', leftCol: 'customer_id', rightCol: 'customer_id' },
        },
    },

    {
        id: 'box-plot-order-by-category',
        level: 2,
        title: 'How do order values compare across product categories?',
        businessContext: `The pricing team wants to understand **whether some product categories consistently command higher order values than others** - and how variable those prices are. A box plot shows the full distribution per category: median, typical range (IQR), and outliers, all in one compact view.`,
        question: 'Create a box plot showing the distribution of order_amount for each product category.',
        goal: 'Box plot - one box per product category, showing the spread of order_amount values.',
        hint: `The box plot needs individual order values per category, not pre-aggregated totals.
1. Select the **orders** table.
2. Set Metric to **MAX** of \`order_amount\`.
3. Add a Group By for **order_id** and a second Group By for **product_category**.
4. Run the query and select the **Box Plot** chart type.
5. In the chart controls, set **X-axis** to \`product_category\` and **Y-axis** to \`max_order_amount\`.`,
        teach: 'A box plot (box-and-whisker chart) summarises a distribution using five statistics: minimum, Q1, median, Q3, and maximum. The box represents the middle 50% of values (IQR), the line inside is the median, and the whiskers extend to the extremes. This makes it easy to compare both central tendency and variability across groups.',
        explanation: 'The box plot reveals not just average order values but also variability - a wide box means inconsistent pricing behaviour, while a narrow box indicates customers in that category buy at predictable price points.',
        expectedChartType: 'boxplot',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: false, join: false, granularity: false, limit: false,
        },
        answer: {
            table: 'orders',
            aggregation: { fn: 'MAX', column: 'order_amount' },
            groupBy: [{ column: 'order_id' }, { column: 'product_category' }],
            filters: [],
            join: null,
        },
    },

    {
        id: 'scatter-volume-vs-revenue',
        level: 2,
        title: 'Which product categories have the highest order volume and revenue?',
        businessContext: `The strategy team wants a single view that shows **both how often each product category is ordered and how much total revenue it generates**. A scatter plot places each category as a dot - the X-axis shows order count, the Y-axis shows total revenue. Categories that appear in the top-right corner are the stars: high volume and high revenue.`,
        question: 'Create a scatter plot with order count (X-axis) vs total revenue (Y-axis), one dot per product category.',
        goal: 'Scatter plot - each dot is a product category, X = order count, Y = total revenue.',
        hint: `Use two metrics and group by product category so each dot represents one category.
1. Select the **orders** table.
2. Add Metric 1: **COUNT** of \`order_id\` (this becomes the X-axis).
3. Add Metric 2: **SUM** of \`order_amount\` (this becomes the Y-axis).
4. Add a Group By for **product_category**.
5. Run the query and select the **Scatter** chart type.
6. In chart controls, set **X (numeric)** to \`count_order_id\` and **Y (numeric)** to \`sum_order_amount\`.`,
        teach: 'Scatter plots are perfect for exploring the relationship between two numeric variables. Each data point represents one entity (here, a product category). The position on X and Y encodes two different metrics simultaneously, making patterns like correlations and outliers immediately visible.',
        explanation: 'The scatter plot creates a 2×2 mental model: top-right = strategic priorities (high volume, high revenue), bottom-right = volume plays, top-left = premium niche, bottom-left = candidates for review.',
        expectedChartType: 'scatter',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: false, join: false, granularity: false, limit: false,
        },
        answer: {
            table: 'orders',
            metrics: [
                { fn: 'COUNT', column: 'order_id' },
                { fn: 'SUM', column: 'order_amount' },
            ],
            groupBy: [{ column: 'product_category' }],
            filters: [],
            join: null,
        },
    },
];

/**
 * Validate user's query state against the expected answer.
 * Uses lenient validation - checks table, join, and chart type only.
 * Returns { passed: boolean, feedback: string }
 */
export function validateAnswer(userQuery, challenge) {
    const { answer, expectedChartType } = challenge;

    // Check table
    if (userQuery.table !== answer.table) {
        return {
            passed: false,
            feedback: `Wrong table. Select the \`${answer.table}\` table.`,
        };
    }

    // Check join (if required)
    if (answer.join) {
        if (!userQuery.join || !userQuery.join.table) {
            return { passed: false, feedback: `You need to add a join.` };
        }
        if (userQuery.join.table !== answer.join.table) {
            return { passed: false, feedback: `Join the \`${answer.join.table}\` table.` };
        }
    } else if (userQuery.join && userQuery.join.table) {
        return { passed: false, feedback: 'This question does not require a join.' };
    }

    // Check chart type
    const typeNames = {
        bar: 'Bar', 'stacked-bar': 'Stacked Bar', line: 'Line',
        area: 'Area', pie: 'Pie', histogram: 'Histogram',
        heatmap: 'Heatmap', map: 'Map', boxplot: 'Box Plot', scatter: 'Scatter',
    };
    if (userQuery.chartType !== expectedChartType) {
        return {
            passed: false,
            feedback: `Wrong chart type. Select the **${typeNames[expectedChartType] || expectedChartType}** chart.`,
        };
    }

    return { passed: true, feedback: '✅ Great work! You built the right visualisation.' };
}
